import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import axios from 'axios';
import { companyInfo } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    message: '',
    contactMethod: 'whatsapp'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      if (formData.contactMethod === 'whatsapp') {
        // Open WhatsApp with pre-filled message
        const message = `您好！我是 ${formData.name}\n\n聯絡電話：${formData.phone}\n電郵：${formData.email}\n項目類型：${formData.projectType}\n\n訊息：\n${formData.message}`;
        const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        setSubmitMessage('正在打開 WhatsApp...');
        setIsSubmitting(false);
      } else {
        // Send email via backend API
        const response = await axios.post(`${API}/contact/submit`, {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          project_type: formData.projectType,
          message: formData.message,
        });
        setSubmitMessage(response.data.message || '您的預約已成功提交，我們會盡快與您聯絡！');
        setIsSubmitting(false);
      }

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          phone: '',
          email: '',
          projectType: '',
          message: '',
          contactMethod: 'whatsapp'
        });
        setSubmitMessage('');
      }, 3000);
    } catch (error) {
      console.error('Submission error:', error);
      const errorMsg = error.response?.data?.detail || '提交失敗，請稍後再試或改用 WhatsApp 聯絡我們。';
      setSubmitMessage(errorMsg);
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="relative py-32 bg-gradient-to-b from-white to-[#f5f5f5] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            聯絡我們
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            讓我們一起打造您夢想中的空間
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                與我們取得聯繫
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                無論您有任何疑問或需要專業的室內設計諮詢，
                我們的團隊隨時準備為您服務。請選擇您偏好的聯絡方式。
              </p>
            </div>

            <div className="space-y-6">
              <motion.a
                href={`https://wa.me/${companyInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-4 rounded-2xl hover:bg-white transition-all duration-300 group"
                whileHover={{ x: 10 }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B35] to-[#9CAF88] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-[#9CAF88] font-medium mb-1">電話 Phone</div>
                  <div className="text-gray-900 font-medium">+852 6991 3856</div>
                </div>
              </motion.a>

              <motion.a
                href={`mailto:${companyInfo.email}`}
                className="flex items-start gap-4 p-4 rounded-2xl hover:bg-white transition-all duration-300 group"
                whileHover={{ x: 10 }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B35] to-[#9CAF88] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-[#9CAF88] font-medium mb-1">電郵 Email</div>
                  <div className="text-gray-900 font-medium break-all">ddt@ddtinteriors.com</div>
                </div>
              </motion.a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    姓名 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all duration-300"
                    placeholder="請輸入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    聯絡電話 *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all duration-300"
                    placeholder="請輸入您的電話號碼"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    電郵地址 *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all duration-300"
                    placeholder="請輸入您的電郵地址"
                  />
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                    項目類型 *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    required
                    value={formData.projectType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all duration-300"
                  >
                    <option value="">請選擇項目類型</option>
                    <option value="office">辦公室</option>
                    <option value="clinic">診所</option>
                    <option value="beauty">美容</option>
                    <option value="retail">零售</option>
                    <option value="pickleball">匹克球運動</option>
                    <option value="other">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    項目詳情 *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all duration-300 resize-none"
                    placeholder="請簡述您的項目需求..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    首選聯絡方式 *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex-1 flex items-center justify-center gap-2 p-3 border-2 border-gray-300 rounded-xl cursor-pointer transition-all duration-300 hover:border-[#FF6B35] has-[:checked]:border-[#FF6B35] has-[:checked]:bg-[#FF6B35]/5">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="whatsapp"
                        checked={formData.contactMethod === 'whatsapp'}
                        onChange={handleChange}
                        className="text-[#FF6B35] focus:ring-[#FF6B35]"
                      />
                      <span className="font-medium">WhatsApp</span>
                    </label>
                    <label className="flex-1 flex items-center justify-center gap-2 p-3 border-2 border-gray-300 rounded-xl cursor-pointer transition-all duration-300 hover:border-[#FF6B35] has-[:checked]:border-[#FF6B35] has-[:checked]:bg-[#FF6B35]/5">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="email"
                        checked={formData.contactMethod === 'email'}
                        onChange={handleChange}
                        className="text-[#FF6B35] focus:ring-[#FF6B35]"
                      />
                      <span className="font-medium">Email</span>
                    </label>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-gradient-to-r from-[#FF6B35] to-[#e55a2b] text-white rounded-xl text-lg font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? (
                    <span>處理中...</span>
                  ) : (
                    <>
                      <span>提交預約</span>
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </motion.button>

                {submitMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-center"
                  >
                    {submitMessage}
                  </motion.div>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
